import { Component, Inject, OnInit } from '@angular/core';
import { NotificationResponse } from '@core/generated/openapi';
import { FormGroup } from '@angular/forms';
import NotificationPriorityEnum = NotificationResponse.NotificationPriorityEnum;
import { UserNotificationsService } from '@services/user-notifications/user-notifications.service';
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef,
} from '@angular/material/dialog';
import moment from 'moment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NotificationServiceUP } from '@services/notifications/notification-service-u-p.service';
import { TodoFromNotificationDialogComponent } from '@modules/tasks/components/todo-from-notification-dialog/todo-from-notification-dialog.component';

import type { Option } from '@rcp/common-web';
import { ButtonStyle, ButtonType } from '@rcp/common-web';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from '@core/material/material.module';
import { COMMON_WEB_IMPORTS } from '@core/modules/common-web-imports';
import { PipesModule } from '@core/pipes/pipes.module';
@UntilDestroy()
@Component({
    selector: 'rcp-oup-user-notifications-dialog',
    templateUrl: './user-notifications-dialog.component.html',
    styleUrls: ['./user-notifications-dialog.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MaterialModule,
        COMMON_WEB_IMPORTS,
        PipesModule,
    ],
    standalone: true,
})
export class UserNotificationsDialogComponent implements OnInit {
    notification: NotificationResponse;
    isClearMode: boolean = false;
    STATUSES = NotificationResponse.StatusEnum;
    PRIORITIES = NotificationResponse.NotificationPriorityEnum;
    isSubmitInProcess: boolean = false;

    formGroup: FormGroup;

    priorityOptions: Option[] = [
        {
            value: NotificationPriorityEnum.Normal,
            label: 'Normal',
        },
        {
            value: NotificationPriorityEnum.High,
            label: 'High',
        },
    ];

    selectedPriority: NotificationPriorityEnum;

    constructor(
        private userNotificationService: UserNotificationsService,
        private dialogRef: MatDialogRef<UserNotificationsDialogComponent>,
        private alertService: NotificationServiceUP,
        private dialog: MatDialog,
        @Inject(MAT_DIALOG_DATA) public data: any,
    ) {}

    ngOnInit(): void {
        this.notification = this.data.notification;
        this.loadForm();
        if (this.data.isClearMode) {
            this.switchToClearMode();
        }
    }

    loadForm() {
        this.formGroup = this.userNotificationService.getEditNotificationForm(
            this.notification,
        );
        this.selectedPriority = this.notification.notificationPriority;
    }

    submitClear() {
        this.isSubmitInProcess = true;
        this.userNotificationService
            .clearNotification(this.formGroup)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (result) => {
                    this.isSubmitInProcess = false;
                    this.alertService.showSuccessMessage(
                        'Notification Status Updated',
                        //'Notification status has been updated to ‘Cleared’.',
                        '',
                    );
                    this.close(result);
                },
                error: (error) => {
                    this.isSubmitInProcess = false;
                    this.alertService.showError(
                        'Failed to clear Notification',
                        error.error,
                    );
                },
            });
    }

    switchToClearMode() {
        this.isClearMode = true;
        this.formGroup?.controls['clearNote']?.enable();
    }

    createToDo(record: NotificationResponse) {
        this.dialog
            .open(TodoFromNotificationDialogComponent, {
                data: { notification: record },
                width: '950px',
            })
            .afterClosed()
            .subscribe((result) => {
                if (result) {
                    this.close(result);
                }
            });
    }

    close(result?: any) {
        this.dialogRef.close(result);
    }

    getCreatedOnMessage(record: NotificationResponse): string {
        const createdDate = moment(record.createdOn).utc().startOf('day');
        const now = moment().utc().startOf('day');
        const daysSince = createdDate.diff(now, 'days');

        if (daysSince === -1) {
            return `1 Day`;
        } else if (daysSince < 0) {
            return `${Math.abs(daysSince)} Days`;
        } else if (daysSince === 0) {
            return 'Created Today';
        } else {
            return ``;
        }
    }

    getCreatedOnIcon(record: NotificationResponse): string {
        const createdDate = moment(record.createdOn).utc().startOf('day');
        const now = moment().utc().startOf('day');
        const daysSince = createdDate.diff(now, 'days');

        if (daysSince < -7) {
            return 'fa-regular fa-calendar-exclamation';
        } else if (daysSince === 0) {
            return 'fa-regular fa-calendar-day';
        } else if (daysSince <= -1) {
            return 'fa-regular fa-calendar-range';
        } else {
            return `fa-regular fa-calendar-range`;
        }
    }

    getCreatedOnClass(record: NotificationResponse): string {
        const createdDate = moment(record.createdOn).utc().startOf('day');
        const now = moment().utc().startOf('day');
        const daysSince = createdDate.diff(now, 'days');

        if (daysSince < -7) {
            return 'date-notice-overdue';
        } else {
            return `date-notice-future`;
        }
    }

    protected readonly ButtonStyle = ButtonStyle;
    protected readonly ButtonType = ButtonType;
}
